import { Box, Stack, Typography } from "@mui/material";
import * as _ from "lodash";
import moment from "moment-timezone";
import { ChangeEvent, useState } from "react";
import { DefaultCalendlyEventTypeSelector } from "../../../components/calendly";
import { AppAlert, DefaultButton, PrimaryButton } from "../../../components/form";
import { FormHelperTextError } from "../../../components/form/errors";
import { SlotSelectionModal } from "../../../components/invitations";
import FlexBox from "../../../components/layouts/Flexbox";
import { AddMembersModal } from "../../../components/modal/add-members-modal";
import {
  DATE_FORMATS,
  getFromAndToDateTimeRange,
} from "../../../services/utils/date-and-time.utils";
import { useAppSelector } from "../../../store";
import { ManualSlotsSection } from "./ManualSlotsSection";
import { SlotSelectionModeToggle } from "./SlotSelectionModeToggle";
import { SlotModalState, VendorSlot } from "./types";

const validateVendorSuggestedSlots = (item: { slots: VendorSlot[] }) => {
  let isFormValid = true;
  let slotSelectionCount = 0;

  const isAllSlotsEmpty = item.slots.every(
    (itemslot) => !itemslot.selectedSlotTimeFrom && !itemslot.selectedSlotTimeTo
  );

  if (isAllSlotsEmpty) {
    return {
      updatedList: item,
      isFormValid: false,
      formMessage: "Please select the times",
    };
  }

  item.slots.forEach((slotsItem, slotsIndex) => {
    const overLapIndex: number[] = [];

    if (slotsItem.selectedSlotTimeFrom && slotsItem.selectedSlotTimeTo) {
      slotSelectionCount++;
    }

    item.slots.forEach((slotsItemInner, slotsIndexInner) => {
      if (
        slotsIndexInner !== slotsIndex &&
        slotsItem.selectedSlotTimeFrom &&
        slotsItem.selectedSlotTimeTo &&
        slotsItemInner.selectedSlotTimeFrom &&
        slotsItemInner.selectedSlotTimeTo &&
        moment(slotsItem.selectedSlotTimeFrom).diff(
          moment(slotsItemInner.selectedSlotTimeFrom),
          "minutes"
        ) === 0 &&
        moment(slotsItem.selectedSlotTimeTo).diff(
          moment(slotsItemInner.selectedSlotTimeTo),
          "minutes"
        ) === 0
      ) {
        overLapIndex.push(slotsIndexInner + 1);
      }
    });

    if (overLapIndex.length > 0) {
      slotsItem.error = `Matching with slot ${overLapIndex.join(",")}`;
      isFormValid = false;
    } else if (
      (slotsItem.selectedSlotTimeTo && !slotsItem.selectedSlotTimeFrom) ||
      (!slotsItem.selectedSlotTimeTo && slotsItem.selectedSlotTimeFrom)
    ) {
      slotsItem.error = "From and To date are required";
      isFormValid = false;
    } else {
      slotsItem.error = "";
    }
  });

  const isSlotsValid = item.slots.every((slot) => !slot.error);

  if (slotSelectionCount < 1) {
    return {
      updatedList: item,
      isFormValid: false,
      formMessage: "Please select at least 1 time",
    };
  }

  return {
    updatedList: item,
    isFormValid: isSlotsValid,
    formMessage: isSlotsValid ? "" : "Please check all the selected times",
  };
};

interface VendorSuggestSlotsProps {
  onCancel: () => void;
  invitationId: string;
  projectDetails: any;
  onSubmit: (
    slots: VendorSlot[],
    additionalMembers?: Array<{ email: string }>
  ) => void;
  onSendCalendlyInvite?: () => void | Promise<any>;
}

export default function VendorSuggestSlots({
  onCancel,
  invitationId,
  projectDetails,
  onSubmit,
  onSendCalendlyInvite,
}: VendorSuggestSlotsProps) {
  const userData = useAppSelector((state) => state.auth.user);
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const ambassadorTimezoneValue = projectDetails?.ambassador?.timezone?.value;
  const ambassadorTimezoneAbbr = ambassadorTimezoneValue
    ? moment().tz(ambassadorTimezoneValue).format("z")
    : undefined;

  const [slotsState, setSlotsState] = useState<{ slots: VendorSlot[] }>({
    slots: [],
  });
  const [slotModalState, setSlotModalState] = useState<SlotModalState>(null);
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [slotSelectionMode, setSlotSelectionMode] = useState<
    "calendly" | "manual"
  >(userData?.isCalendly ? "calendly" : "manual");

  const initialEmails =
    projectDetails?.additionalMembers
      ?.map((member: any) => member?.email)
      .filter(Boolean) || [];
  const [emailIds, setEmailIds] = useState<{
    list: string[];
    invalidEmailIds: string[];
  }>({
    list: initialEmails,
    invalidEmailIds: [],
  });
  const [soloAttendanceChecked, setSoloAttendanceChecked] = useState(
    initialEmails.length === 0
  );
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<
    "manual" | "calendly" | null
  >(null);
  const [attendeesConfirmed, setAttendeesConfirmed] = useState(
    initialEmails.length > 0
  );

  const slots: VendorSlot[] = slotsState?.slots || [];
  const manualSelectionEnabled =
    !userData?.isCalendly || slotSelectionMode === "manual";
  const isCalendlyMode =
    Boolean(userData?.isCalendly) && slotSelectionMode === "calendly";

  const buildSlot = (from: Date, to: Date, isTimeRange: boolean): VendorSlot => {
    const formatInAmbassadorTimezone = (value: Date): string | undefined => {
      if (!value) return undefined;
      return ambassadorTimezoneValue
        ? moment(value)
            .tz(ambassadorTimezoneValue)
            .format(DATE_FORMATS.fullDateWithTime)
        : moment(value).format(DATE_FORMATS.fullDateWithTime);
    };

    return {
      selectedSlotTimeFrom: from,
      selectedSlotTimeTo: to,
      isTimeRange,
      error: "",
      convertedSlotTimeFrom: formatInAmbassadorTimezone(from),
      convertedSlotTimeTo: formatInAmbassadorTimezone(to),
    };
  };

  const commitSlot = (slot: VendorSlot, modalState: SlotModalState) => {
    const listClone = _.cloneDeep(slotsState);

    if (modalState?.mode === "edit" && modalState.index !== null) {
      listClone.slots[modalState.index] = slot;
    } else {
      listClone.slots = [...(listClone.slots || []), slot];
    }

    if (submitClicked) {
      const validation = validateVendorSuggestedSlots(listClone);
      setSlotsState(validation.updatedList);
      setFormErrorMessage(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setSlotsState(listClone);
      setFormErrorMessage("");
    }
  };

  const commitSlots = (nextSlots: VendorSlot[]) => {
    if (nextSlots.length === 0) return;
    const listClone = _.cloneDeep(slotsState);
    listClone.slots = [...(listClone.slots || []), ...nextSlots];
    if (submitClicked) {
      const validation = validateVendorSuggestedSlots(listClone);
      setSlotsState(validation.updatedList);
      setFormErrorMessage(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setSlotsState(listClone);
      setFormErrorMessage("");
    }
  };

  const handleSlotSubmit = (payload: any) => {
    if (!slotModalState) return;

    const payloads = Array.isArray(payload) ? payload : [payload];
    const nextSlots: VendorSlot[] = [];

    for (const p of payloads) {
      const { dateFrom, dateTo } = getFromAndToDateTimeRange(p);
      nextSlots.push(buildSlot(dateFrom, dateTo, p?.type === "datetimerange"));
    }

    if (isUpdatingAvailability) {
      const nextState = { slots: nextSlots };
      if (submitClicked) {
        const validation = validateVendorSuggestedSlots(nextState);
        setSlotsState(validation.updatedList);
        setFormErrorMessage(
          validation.isFormValid ? "" : validation.formMessage
        );
      } else {
        setSlotsState(nextState);
        setFormErrorMessage("");
      }
      setSlotModalState(null);
      setIsUpdatingAvailability(false);
      return;
    }

    if (nextSlots.length === 1) commitSlot(nextSlots[0], slotModalState);
    else commitSlots(nextSlots);
    setSlotModalState(null);
  };

  const handleOnSlotAdd = () => {
    setIsUpdatingAvailability(slots.length > 0);
    setSlotModalState({ mode: "create", index: null });
  };

  const handleOnSlotEdit = (index: number) => {
    setSlotModalState({ mode: "edit", index });
  };

  const handleOnSlotRemove = (index: number) => {
    const clonedList = _.cloneDeep(slotsState);
    clonedList.slots.splice(index, 1);

    const validation = validateVendorSuggestedSlots(clonedList);
    setSlotsState(validation.updatedList);

    if (submitClicked) {
      setFormErrorMessage(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setFormErrorMessage("");
    }
  };

  const validateAttendeeRequirement = (): boolean => {
    const hasValidEmails =
      emailIds.list.length > 0 && emailIds.invalidEmailIds.length === 0;
    const hasSoloAttendance = soloAttendanceChecked;

    return hasValidEmails || hasSoloAttendance;
  };

  const handleSlotSelectionModeChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const nextMode = event.target.value as "calendly" | "manual";
    setSlotSelectionMode(nextMode);
    setFormErrorMessage("");
    setSubmitClicked(false);
  };

  const proceedWithManualSubmission = () => {
    setSubmitClicked(true);

    const validation = validateVendorSuggestedSlots(slotsState);
    setSlotsState(validation.updatedList);

    if (!validation.isFormValid) {
      setFormErrorMessage(validation.formMessage);
      return;
    }

    setFormErrorMessage("");
    setSubmitting(true);

    const additionalMembers =
      emailIds.list.length > 0
        ? emailIds.list.map((email) => ({ email }))
        : undefined;

    onSubmit(validation.updatedList.slots, additionalMembers);
  };

  const proceedWithCalendlySubmission = async () => {
    setFormErrorMessage("");
    setSubmitting(true);
    try {
      await onSendCalendlyInvite?.();
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMembersSubmit = async (data: {
    emails: string[];
    soloAttendance: boolean;
  }) => {
    const wasPendingSubmission = pendingSubmission;

    setEmailIds({
      list: data.emails,
      invalidEmailIds: [],
    });
    setSoloAttendanceChecked(data.soloAttendance);
    setShowAddMembersModal(false);
    setFormErrorMessage("");
    setPendingSubmission(null);
    setAttendeesConfirmed(true);

    if (wasPendingSubmission === "calendly") {
      await proceedWithCalendlySubmission();
    } else if (wasPendingSubmission === "manual") {
      proceedWithManualSubmission();
    }
  };

  const handleSubmit = async () => {
    setSubmitClicked(true);

    if (!isCalendlyMode) {
      const validation = validateVendorSuggestedSlots(slotsState);
      setSlotsState(validation.updatedList);

      if (!validation.isFormValid) {
        setFormErrorMessage(validation.formMessage);
        return;
      }
    }

    if (!validateAttendeeRequirement()) {
      setFormErrorMessage(
        "Please add attendee email addresses or confirm that no additional representatives will join"
      );
      setPendingSubmission(isCalendlyMode ? "calendly" : "manual");
      setShowAddMembersModal(true);
      return;
    }

    if (isCalendlyMode) {
      proceedWithCalendlySubmission();
      return;
    }

    proceedWithManualSubmission();
  };

  return (
    <>
      <Stack flex={1} height="100%">
        <Box flex={1} overflow="auto">
          <>
            {userData?.isCalendly && (
              <Box mb={3}>
                <SlotSelectionModeToggle
                  value={slotSelectionMode}
                  onChange={handleSlotSelectionModeChange}
                />
              </Box>
            )}

            {isCalendlyMode && (
              <Box mb={3}>
                <DefaultCalendlyEventTypeSelector
                  enabled
                  formLabelId="vendor-suggest-calendly-event-type-label"
                  description="Your Calendly invite will use this event type. You can change it before sending."
                />
              </Box>
            )}

            {manualSelectionEnabled && (
              <Box mb={3}>
                <AppAlert severity="info" sx={{ fontSize: "small" }}>
                  Please enter the times according to your browser's time zone;
                  they will be automatically converted to the ambassador's time
                  zone. Add at least 1 slot.
                </AppAlert>
              </Box>
            )}

            {formErrorMessage && (
              <Box mb={2}>
                <FormHelperTextError
                  showIcon
                  error={
                    formErrorMessage ===
                    "Please add attendee email addresses or confirm that no additional representatives will join"
                      ? undefined
                      : formErrorMessage
                  }
                />
              </Box>
            )}

            {attendeesConfirmed && validateAttendeeRequirement() && (
              <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Attendees
                  </Typography>
                  <PrimaryButton onClick={() => setShowAddMembersModal(true)}>
                    Change Attendees
                  </PrimaryButton>
                </Box>
                {soloAttendanceChecked ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    ✓ No additional representatives will join — you will attend
                    the meeting yourself.
                  </Typography>
                ) : emailIds.list.length > 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    ✓ {emailIds.list.length} attendee
                    {emailIds.list.length > 1 ? "s" : ""} added:{" "}
                    {emailIds.list.map((email, index) => (
                      <span key={email}>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "primary.main",
                            textDecoration: "underline",
                            textDecorationColor: "primary.main",
                          }}
                        >
                          {email}
                        </Typography>
                        {index < emailIds.list.length - 1 && ", "}
                      </span>
                    ))}
                  </Typography>
                ) : null}
              </Box>
            )}

            {manualSelectionEnabled && (
              <ManualSlotsSection
                slots={slots}
                browserTimezone={browserTimezone}
                ambassadorTimezoneAbbr={ambassadorTimezoneAbbr}
                ambassadorTimezoneValue={ambassadorTimezoneValue}
                onAddSlot={handleOnSlotAdd}
                onEditSlot={handleOnSlotEdit}
                onRemoveSlot={handleOnSlotRemove}
                showErrors={submitClicked}
                minimumSlots={1}
                minimumSlotsText={`Add at least 1 slot. Times reflect your timezone (${moment().tz(browserTimezone).format("z")}) and are automatically converted
              to the ambassador's timezone (${ambassadorTimezoneAbbr || "N/A"}).`}
              />
            )}
          </>
        </Box>
        <FlexBox
          justifyContent="flex-end"
          position="sticky"
          bottom={0}
          zIndex={10}
          bgcolor="background.paper"
          borderTop={1}
          borderColor="divider"
          px={2}
          pt={2}
        >
          <FlexBox gap={1}>
            <DefaultButton onClick={onCancel} sx={{ width: { sm: 180 } }}>
              Discard Changes
            </DefaultButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={submitting}
              loading={submitting}
              sx={{ width: { sm: 220 } }}
            >
              {isCalendlyMode ? "Send Calendly Invite" : "Submit"}
            </PrimaryButton>
          </FlexBox>
        </FlexBox>
      </Stack>

      {slotModalState && (
        <SlotSelectionModal
          onClose={() => {
            setSlotModalState(null);
            setIsUpdatingAvailability(false);
          }}
          onSubmit={handleSlotSubmit}
          initialSelectedTimes={slots
            .map((s) => s.selectedSlotTimeFrom)
            .filter(Boolean)}
          slotIndex={
            slotModalState.mode === "edit" && slotModalState.index !== null
              ? slotModalState.index + 1
              : slots.length + 1
          }
          slotDetails={
            slotModalState.mode === "edit" && slotModalState.index !== null
              ? slots[slotModalState.index]
              : null
          }
          oppositeUserTimezone={ambassadorTimezoneValue}
          userRole="vendor"
          allowMultiSelect={
            slotModalState.mode === "create" && slotModalState.index === null
          }
          totalSlots={slots.length}
        />
      )}

      <AddMembersModal
        open={showAddMembersModal}
        onClose={() => {
          setShowAddMembersModal(false);
          setPendingSubmission(null);
        }}
        onSubmit={handleAddMembersSubmit}
        initialEmails={
          emailIds.list.length > 0
            ? emailIds.list
            : projectDetails?.additionalMembers
                ?.map((member: any) => member?.email)
                .filter(Boolean) || []
        }
        initialSoloAttendance={soloAttendanceChecked}
        title="Add Attendees"
        submitButtonText="Continue"
        cancelButtonText="Cancel"
      />
    </>
  );
}
